///
/// EarthBrowser
///
/// Created by Matt Giger on 1/11/2025
/// Copyright (c) 2025 Geodata Labs
///

import { DateField } from "@/components/dialogs/subscription/DateField"
import { Button } from "@/components/ui/button"
import { setSubscribeOpen } from "@/redux/dialogSlice"
import { useAppDispatch } from "@/redux/store"
import { useCreatePortalSessionMutation } from "@/redux/userApi"
import { logger } from "@/utils/LogDispatcher"
import { capitalize } from "@/utils/utils"
import { PricingPlans } from "@shared-types/pricing-plans"
import type { Subscription } from "@shared-types/subscription"
import { CalendarPlus2, Gift } from "lucide-react"

export function SubscriptionCardActive({ subscription }: { subscription: Subscription }) {
	const dispatch = useAppDispatch()
	const [createPortalSession, { isLoading: isPortalLoading }] = useCreatePortalSessionMutation()

	// Stripe-managed subscriptions open the Customer Portal (cancel, update
	// card, invoices); beta grants fall back to the subscribe dialog.
	const isStripeManaged = subscription.customer_id?.startsWith("cus_")

	const handleManage = async () => {
		if (isStripeManaged) {
			try {
				const { url } = await createPortalSession().unwrap()
				window.location.href = url
				return
			} catch (error) {
				logger.error(error)
			}
		}
		dispatch(setSubscribeOpen(true))
	}

	const pricingPlan = PricingPlans[subscription.subscription_id]
	if (!pricingPlan) {
		return null
	}

	const isBetaYearly = subscription.subscription_id === "beta_yearly"
	// A portal cancellation keeps the subscription active with
	// cancel_at_period_end set; access runs until the period end.
	const cancelsAtPeriodEnd = subscription.cancel_at_period_end === true
	// Stripe rows track their end in current_period_end; beta grants use expires_at
	const endsAt = subscription.expires_at ?? subscription.current_period_end
	const daysUntilExpiry = endsAt
		? Math.ceil((new Date(endsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
		: null
	const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 30 && daysUntilExpiry > 0

	return (
		<div className="rounded-lg border border-border bg-background p-3 transition-colors hover:bg-muted/50">
			<div className="flex flex-col gap-2">
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-lg bg-muted">
						{isBetaYearly ? (
							<Gift className="h-4 w-4 text-muted-foreground" />
						) : (
							<CalendarPlus2 className="h-4 w-4 text-muted-foreground" />
						)}
					</div>
					<div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
						<p className="text-sm font-semibold">
							{isBetaYearly ? "Complimentary" : pricingPlan.name}
						</p>
						<span className="text-xs text-muted-foreground">·</span>
						<p className="text-xs text-muted-foreground">Yearly</p>
						{cancelsAtPeriodEnd ? (
							<span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 text-xs font-medium">
								Cancels at period end
							</span>
						) : (
							<span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-medium">
								{capitalize(subscription.status)}
							</span>
						)}
					</div>
				</div>
				<div className="flex items-center justify-between gap-2">
					<div className="flex items-center gap-3 text-xs text-muted-foreground">
						<div className="flex items-center">
							<span>Created</span>
							<DateField value={subscription.created_at} />
						</div>
						{endsAt && (
							<>
								<span>·</span>
								<div className="flex items-center">
									<span className={(isExpiringSoon || cancelsAtPeriodEnd) ? "text-amber-500 font-medium" : ""}>
										{cancelsAtPeriodEnd ? "Access until" : (isStripeManaged ? "Renews" : "Expires")}
									</span>
									<DateField value={endsAt} />
								</div>
							</>
						)}
					</div>
					{(isExpiringSoon || !isBetaYearly) && (
						<Button
							onClick={handleManage}
							disabled={isPortalLoading}
							size="sm"
							variant="outline"
							className="shrink-0"
						>
							{isPortalLoading ? "Opening…" : cancelsAtPeriodEnd ? "Resume" : isExpiringSoon ? "Renew" : "Manage"}
						</Button>
					)}
				</div>
			</div>
		</div>
	)
}
